import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Grid, Card, CardContent, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, Select, MenuItem, FormControl,
  InputLabel, TextField, Button, Chip
} from '@mui/material';
import { PictureAsPdf as PdfIcon, FileDownload as ExcelIcon } from '@mui/icons-material';
import api from '../services/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmtINR = (n) => `₹${(parseFloat(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Role color palette — consistent across Finance Hub
const ROLE_COLORS = {
  actor:           { bg: '#e3f2fd', text: '#0d47a1', border: '#90caf9' },
  director:        { bg: '#fce4ec', text: '#880e4f', border: '#f48fb1' },
  editor:          { bg: '#e8f5e9', text: '#1b5e20', border: '#a5d6a7' },
  cinematographer: { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  writer:          { bg: '#f3e5f5', text: '#4a148c', border: '#ce93d8' },
  owner:           { bg: '#eceff1', text: '#263238', border: '#90a4ae' },
  manager:         { bg: '#e0f2f1', text: '#004d40', border: '#80cbc4' },
  hr:              { bg: '#fff8e1', text: '#f57f17', border: '#ffe082' },
  lead:            { bg: '#ede7f6', text: '#311b92', border: '#b39ddb' },
  finance:         { bg: '#e8eaf6', text: '#1a237e', border: '#9fa8da' },
  driver:          { bg: '#f5f5f5', text: '#424242', border: '#bdbdbd' },
};

const RoleTag = ({ role, size = 'small' }) => {
  const c = ROLE_COLORS[role?.toLowerCase()] || { bg: '#f5f5f5', text: '#424242', border: '#bdbdbd' };
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center',
      px: size === 'small' ? 1 : 1.5,
      py: size === 'small' ? 0.25 : 0.5,
      borderRadius: '4px',
      border: `1px solid ${c.border}`,
      bgcolor: c.bg, color: c.text,
      fontSize: size === 'small' ? '0.7rem' : '0.8rem',
      fontWeight: 600, lineHeight: 1.5,
      textTransform: 'capitalize',
      mr: 0.5, mb: 0.25,
    }}>
      {role}
    </Box>
  );
};

function SummaryCard({ label, value, sub, color = '#1a237e', bg = '#e8eaf6' }) {
  return (
    <Card sx={{ bgcolor: bg, borderLeft: `5px solid ${color}`, height: '100%' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color }}>{value}</Typography>
        <Typography variant="body2" fontWeight={600} color="text.primary">{label}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function FinanceHub() {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isActive, setIsActive] = useState('true');
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));

  const fetchData = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/users/finance-hub', { params: { isActive, month, year } });
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load Finance Hub data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [isActive, month, year]); // eslint-disable-line

  const exportWorkforcePDF = () => {
    if (!data) return;
    const employees = filteredEmployees;
    const rows = employees.map(e => `
      <tr>
        <td>${e.employeeId||''}</td>
        <td>${e.fullName||''}</td>
        <td>${e.roles.join(', ')}</td>
        <td>${e.gender}</td>
        <td>${e.partTime ? 'Part-Time' : 'Full-Time'}</td>
        <td>${fmtINR(e.effectiveSalary ?? e.salary)}</td>
        <td>${fmtDate(e.dateOfJoining)}</td>
        <td>${e.isActive ? 'Active' : 'Inactive'}</td>
      </tr>`).join('');
    const total = employees.reduce((s, e) => s + (e.effectiveSalary ?? e.salary), 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>JBDE Workforce Report</title>
<style>
  body{font-family:Arial,sans-serif;margin:24px;font-size:12px;}
  h2{text-align:right;color:#1a237e;margin:0 0 2px;}
  .meta{text-align:right;color:#555;font-size:11px;margin-bottom:12px;}
  .summary{display:flex;gap:20px;background:#e8eaf6;padding:10px;border-radius:4px;margin-bottom:14px;}
  .summary div{display:flex;flex-direction:column;}
  .summary .val{font-size:18px;font-weight:700;color:#1a237e;}
  .summary .lbl{font-size:11px;color:#555;}
  table{width:100%;border-collapse:collapse;}
  th,td{border:1px solid #aaa;padding:5px 7px;text-align:left;}
  th{background:#e8eaf6;font-weight:700;color:#1a237e;}
  tr:nth-child(even) td{background:#f9f9f9;}
  tfoot td{font-weight:700;background:#e8eaf6;}
</style></head><body>
<h2>JBDE – Workforce Report</h2>
<div class="meta">Generated: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })} &nbsp;·&nbsp; Status: ${isActive === 'true' ? 'Active' : isActive === 'false' ? 'Inactive' : 'All'}</div>
<div class="summary">
  <div><span class="val">${employees.length}</span><span class="lbl">Total</span></div>
  <div><span class="val">${employees.filter(e=>!e.partTime).length}</span><span class="lbl">Full-Time</span></div>
  <div><span class="val">${employees.filter(e=>e.partTime).length}</span><span class="lbl">Part-Time</span></div>
  <div><span class="val">${fmtINR(total)}</span><span class="lbl">Monthly Salary Burn</span></div>
</div>
<table>
  <thead><tr><th>Emp ID</th><th>Name</th><th>Role(s)</th><th>Gender</th><th>Type</th><th>Salary</th><th>Joined</th><th>Status</th></tr></thead>
  <tbody>${rows}</tbody>
  <tfoot><tr><td colspan="5">TOTAL</td><td>${fmtINR(total)}</td><td colspan="2"></td></tr></tfoot>
</table>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
    w.document.title = 'JBDE Workforce Report';
    setTimeout(() => w.print(), 400);
  };

  const exportWorkforceExcel = () => {
    if (!data) return;
    const employees = filteredEmployees;

    // Sheet 1 — Employee List
    const empRows = employees.map(e => ({
      'Emp ID': e.employeeId || '',
      'Name': e.fullName || '',
      'Primary Role': e.primaryRole || '',
      'All Roles': e.roles.join(', '),
      'Gender': e.gender,
      'Type': e.partTime ? 'Part-Time' : 'Full-Time',
      'Working Days/Month': e.partTime ? (e.partTimeDaysPerMonth || '') : '',
      'Base Salary (₹)': e.salary,
      'Effective Salary (₹)': e.effectiveSalary ?? e.salary,
      'Date of Joining': e.dateOfJoining ? new Date(e.dateOfJoining + 'T00:00:00').toLocaleDateString('en-IN') : '',
      'Status': e.isActive ? 'Active' : 'Inactive',
    }));

    // Sheet 2 — Role Breakdown
    const roleRows = (data.workforce.roleBreakdown || []).map(r => ({
      'Role': r.role,
      'Full-Time': r.fullTime,
      'Part-Time': r.partTime,
      'Total': r.total,
      'Monthly Salary (₹)': r.salary,
    }));

    // Sheet 3 — Gender Breakdown
    const genderRows = Object.entries(data.workforce.genderBreakdown || {})
      .filter(([, v]) => v.fullTime + v.partTime > 0)
      .map(([g, v]) => ({
        'Gender': g,
        'Full-Time': v.fullTime,
        'Part-Time': v.partTime,
        'Total': v.fullTime + v.partTime,
        'Monthly Salary (₹)': v.salary,
      }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(empRows), 'Employees');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(roleRows), 'By Role');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(genderRows), 'By Gender');

    const statusLabel = isActive === 'true' ? 'Active' : isActive === 'false' ? 'Inactive' : 'All';
    XLSX.writeFile(wb, `JBDE_Workforce_${MONTHS[parseInt(month)-1]}_${year}_${statusLabel}.xlsx`);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const { workforce, payroll, advances, expenses, awards } = data;

  // Apply local filters to employee list — Role filter matches PRIMARY role only
  const filteredEmployees = (workforce.employees || []).filter(e => {
    if (roleFilter && e.primaryRole !== roleFilter) return false;
    if (typeFilter === 'fulltime' && e.partTime) return false;
    if (typeFilter === 'parttime' && !e.partTime) return false;
    if (genderFilter && e.gender !== genderFilter) return false;
    return true;
  });

  const allRoles = [...new Set((workforce.employees||[]).map(e => e.primaryRole).filter(Boolean))].sort();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Finance Hub</Typography>
          <Typography variant="body2" color="text.secondary">Company workforce cost, payroll & expense overview</Typography>
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
              {MONTHS.map((m, i) => <MenuItem key={m} value={String(i+1)}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Year" type="number" value={year}
            onChange={e => setYear(e.target.value)} sx={{ width: 80 }} />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Status</InputLabel>
            <Select value={isActive} label="Status" onChange={e => setIsActive(e.target.value)}>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
              <MenuItem value="">Both</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Top summary strip */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}><SummaryCard label="Total Employees" value={workforce.total} color="#1a237e" bg="#e8eaf6" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard label="Full-Time" value={workforce.fullTime} color="#2e7d32" bg="#e8f5e9" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard label="Part-Time" value={workforce.partTime} color="#e65100" bg="#fff3e0" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard label="Monthly Salary Burn" value={fmtINR(workforce.monthlyBurn)} sub="Budgeted" color="#6a1b9a" bg="#f3e5f5" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard label="Expenses (Period)" value={fmtINR(expenses.total)} sub={`${MONTHS[parseInt(month)-1]} ${year}`} color="#c62828" bg="#ffebee" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard label="Advances Pending" value={fmtINR(advances.totalPending)} sub={`${advances.total} active`} color="#37474f" bg="#eceff1" /></Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {['💼 Workforce', '💰 Payroll', '📊 Expenses', '📈 Advances'].map((label, i) => (
            <Tab key={i} label={label} sx={{ fontWeight: 600, textTransform: 'none', fontSize: '0.85rem' }} />
          ))}
        </Tabs>
      </Box>

      {/* ── TAB 0: WORKFORCE ─────────────────────────────────────────── */}
      {tab === 0 && (
        <Box>
          {/* Role breakdown */}
          <Typography variant="subtitle1" fontWeight={700} mb={1}>By Role</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  {['Role','Full-Time','Part-Time','Total','Monthly Salary'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {workforce.roleBreakdown.map((r, i) => (
                  <TableRow key={r.role} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                    <TableCell><RoleTag role={r.role} /></TableCell>
                    <TableCell>{r.fullTime}</TableCell>
                    <TableCell>{r.partTime}</TableCell>
                    <TableCell><strong>{r.total}</strong></TableCell>
                    <TableCell sx={{ color: '#6a1b9a', fontWeight: 600 }}>{fmtINR(r.salary)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  <TableCell><strong>TOTAL</strong></TableCell>
                  <TableCell><strong>{workforce.fullTime}</strong></TableCell>
                  <TableCell><strong>{workforce.partTime}</strong></TableCell>
                  <TableCell><strong>{workforce.total}</strong></TableCell>
                  <TableCell sx={{ color: '#6a1b9a', fontWeight: 700 }}>{fmtINR(workforce.monthlyBurn)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Gender breakdown */}
          <Typography variant="subtitle1" fontWeight={700} mb={1}>By Gender</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  {['Gender','Full-Time','Part-Time','Total','Monthly Salary'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(workforce.genderBreakdown).filter(([,v]) => v.fullTime + v.partTime > 0).map(([g, v], i) => (
                  <TableRow key={g} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                    <TableCell>{g}</TableCell>
                    <TableCell>{v.fullTime}</TableCell>
                    <TableCell>{v.partTime}</TableCell>
                    <TableCell><strong>{v.fullTime + v.partTime}</strong></TableCell>
                    <TableCell sx={{ color: '#6a1b9a', fontWeight: 600 }}>{fmtINR(v.salary)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Employee list with filters */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={700}>Employee List ({filteredEmployees.length})</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Role</InputLabel>
                <Select value={roleFilter} label="Role" onChange={e => setRoleFilter(e.target.value)}>
                  <MenuItem value="">All Roles</MenuItem>
                  {allRoles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="fulltime">Full-Time</MenuItem>
                  <MenuItem value="parttime">Part-Time</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Gender</InputLabel>
                <Select value={genderFilter} label="Gender" onChange={e => setGenderFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <Button size="small" variant="outlined" startIcon={<PdfIcon />} onClick={exportWorkforcePDF}>
                Export PDF
              </Button>
              <Button size="small" variant="outlined" startIcon={<ExcelIcon />} onClick={exportWorkforceExcel}
                sx={{ borderColor: '#2e7d32', color: '#2e7d32', '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' } }}>
                Export Excel
              </Button>
            </Box>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  {['Emp ID','Name','Role(s)','Gender','Type','Monthly Salary','Joined','Status'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: '0.75rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((e, i) => (
                  <TableRow key={e.id} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'primary.main', fontWeight: 500 }}>{e.employeeId}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{e.fullName}</TableCell>
                    <TableCell><Box display="flex" flexWrap="wrap">{e.roles.map(r => <RoleTag key={r} role={r} />)}</Box></TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{e.gender}</TableCell>
                    <TableCell><Chip label={e.partTime ? 'Part-Time' : 'Full-Time'} size="small" color={e.partTime ? 'warning' : 'success'} variant="outlined" /></TableCell>
                    <TableCell sx={{ color: '#6a1b9a', fontWeight: 600, fontSize: '0.8rem' }}>
                      {fmtINR(e.effectiveSalary ?? e.salary)}
                      {e.partTime && e.partTimeDaysPerMonth && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {e.partTimeDaysPerMonth}d/mo · base {fmtINR(e.salary)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{fmtDate(e.dateOfJoining)}</TableCell>
                    <TableCell><Chip label={e.isActive ? 'Active' : 'Inactive'} size="small" color={e.isActive ? 'success' : 'default'} /></TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  <TableCell colSpan={5}><strong>TOTAL</strong></TableCell>
                  <TableCell sx={{ color: '#6a1b9a', fontWeight: 700 }}>{fmtINR(filteredEmployees.reduce((s,e)=>s+(e.effectiveSalary??e.salary),0))}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── TAB 1: PAYROLL ───────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Monthly Payroll Trend (last 6 months)</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  {['Month','Employees Paid','Total Basic','Total Earned','Total Net Paid'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {payroll.trend.length === 0
                  ? <TableRow><TableCell colSpan={5} align="center">No payroll data</TableCell></TableRow>
                  : payroll.trend.map((p, i) => (
                    <TableRow key={p.monthYear} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                      <TableCell fontWeight={600}>{p.monthYear}</TableCell>
                      <TableCell>{p.count}</TableCell>
                      <TableCell>{fmtINR(p.totalBasic)}</TableCell>
                      <TableCell>{fmtINR(p.totalEarned)}</TableCell>
                      <TableCell sx={{ color: '#2e7d32', fontWeight: 700 }}>{fmtINR(p.totalNet)}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Payroll Records — {MONTHS[parseInt(month)-1]} {year}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  {['Employee','Basic','Earned','Award','Adv. Ded.','Net Paid','Mode'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: '0.75rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {payroll.records.filter(p => p.monthYear === `${MONTHS[parseInt(month)-1]}-${year}`).length === 0
                  ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No paid records for {MONTHS[parseInt(month)-1]} {year}</TableCell></TableRow>
                  : payroll.records.filter(p => p.monthYear === `${MONTHS[parseInt(month)-1]}-${year}`).map((p, i) => (
                    <TableRow key={p.employeeCode} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{p.employeeName}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{fmtINR(p.basicSalary)}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{fmtINR(p.earnedSalary)}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: '#1565c0' }}>{fmtINR(p.awardAmount)}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: '#c62828' }}>-{fmtINR(p.advanceDeduction)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#2e7d32' }}>{fmtINR(p.netPayable)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{p.paymentMode || '—'}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── TAB 2: EXPENSES ──────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}><SummaryCard label="Total Expenses" value={fmtINR(expenses.total)} sub={`${MONTHS[parseInt(month)-1]} ${year}`} color="#c62828" bg="#ffebee" /></Grid>
            <Grid item xs={12} sm={4}><SummaryCard label="Awards / Bonuses" value={fmtINR(awards.total)} sub={`${awards.count} awards paid`} color="#6a1b9a" bg="#f3e5f5" /></Grid>
            <Grid item xs={12} sm={4}><SummaryCard label="Est. Total Cost" value={fmtINR(workforce.monthlyBurn + expenses.total + awards.total)} sub="Salary + Expenses + Awards" color="#37474f" bg="#eceff1" /></Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight={700} mb={1}>Expenses by Category</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>% of Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(expenses.byCategory).sort(([,a],[,b]) => b-a).map(([cat, amt], i) => (
                  <TableRow key={cat} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                    <TableCell>{cat}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{fmtINR(amt)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: `${Math.round((amt/expenses.total)*100)}%`, maxWidth: 120, height: 6, bgcolor: '#c62828', borderRadius: 1 }} />
                        <Typography variant="caption">{Math.round((amt/expenses.total)*100)}%</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {Object.keys(expenses.byCategory).length === 0 && (
                  <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No expenses for {MONTHS[parseInt(month)-1]} {year}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── TAB 3: ADVANCES ──────────────────────────────────────────── */}
      {tab === 3 && (
        <Box>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}><SummaryCard label="Active Advances" value={advances.total} color="#37474f" bg="#eceff1" /></Grid>
            <Grid item xs={12} sm={4}><SummaryCard label="Total Outstanding" value={fmtINR(advances.totalPending)} sub="Pending recovery" color="#c62828" bg="#ffebee" /></Grid>
          </Grid>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  {['Employee','Total Advance','Monthly Deduction','Reason','Date'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: '0.75rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {advances.records.length === 0
                  ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No active advances</TableCell></TableRow>
                  : advances.records.map((a, i) => (
                    <TableRow key={a.id} sx={{ bgcolor: i%2===0?'#fff':'#f9f9f9' }}>
                      <TableCell sx={{ fontWeight: 500 }}>{a.employee?.fullName || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#c62828' }}>{fmtINR(a.totalAmount)}</TableCell>
                      <TableCell>{fmtINR(a.monthlyDeduction)}/mo</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{a.reason || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{fmtDate(a.createdAt)}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
